import {
  DeletePayload,
  PostPayload,
  PutPayload,
} from "../../../model/http-crud-models";
import { httpClient } from "../../../service/http";
import {
  AdyenAccountHolder,
  AdyenAccountHolderListItem,
  AdyenAccountHolderOnboardingLink,
  AdyenAccountHolderVerificationError,
  CreateAdyenAccountHolderPayload,
  UpdateAdyenAccountHolderPayload,
} from "../models/adyen-account-holder";
import { AdyenAccountHolderPaymentInstrument } from "../models/adyen-account-holder-payment-instrument";
import {
  AdyenBalanceAccount,
  AdyenBalanceAccountListItem,
  MutateAdyenBalanceAccountPayload,
} from "../models/adyen-balance-account";
import {
  AdyenBusinessLine,
  AdyenBusinessLineListItem,
  AdyenStore,
  AdyenStoreListItem,
  BusinessLineIndustryCode,
  MutateAdyenBusinessLinePayload,
  MutateAdyenStorePayload,
} from "../models/adyen-business-line";
import {
  AdyenSplitConfiguration,
  AdyenSplitConfigurationListItem,
} from "../models/adyen-split-configuration";

export const adyenAccountHoldersHttp = {
  getAdyenAccountHolders: async () => {
    return httpClient<AdyenAccountHolderListItem[]>({
      method: "GET",
      url: "/management/adyen/platform/account-holders/items",
    });
  },

  getAdyenAccountHolder: async (id?: string) => {
    return httpClient<AdyenAccountHolder>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}`,
    });
  },

  pushAdyenAccountHolderToAdyen: async ({
    data: { id },
  }: Omit<PostPayload<{ id: string }>, "organizationKeyName">) => {
    return httpClient({
      method: "POST",
      url: `/management/adyen/platform/account-holders/items/${id}/push-to-adyen`,
    });
  },

  createAdyenAccountHolder: async ({
    data: { pushToAdyen, ...rest },
  }: Omit<
    PostPayload<CreateAdyenAccountHolderPayload>,
    "organizationKeyName"
  >) => {
    const params = new URLSearchParams([
      ["pushToAdyen", pushToAdyen.toString()],
    ]);

    return httpClient({
      method: "POST",
      url: `/management/adyen/platform/account-holders/items?${params.toString()}`,
      data: rest,
    });
  },

  updateAdyenAccountHolder: async ({
    data: { id, ...rest },
  }: Omit<
    PutPayload<UpdateAdyenAccountHolderPayload>,
    "organizationKeyName"
  >) => {
    return httpClient({
      method: "PUT",
      url: `/management/adyen/platform/account-holders/items/${id}`,
      data: rest,
    });
  },

  getVerificationErrors: async (id?: string) => {
    return httpClient<AdyenAccountHolderVerificationError[]>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}/verification-errors`,
    });
  },

  getOnboardingLink: async (id?: string) => {
    return httpClient<AdyenAccountHolderOnboardingLink>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}/onboarding-link`,
    });
  },

  getCapabilities: async (id?: string) => {
    return httpClient<Object>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}/capabilities`,
    });
  },

  getBusinessLines: async (accountHolderId?: string) => {
    return httpClient<AdyenBusinessLineListItem[]>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${accountHolderId}/business-lines/items`,
    });
  },

  getBusinessLine: async (accountHolderId?: string, id?: string) => {
    return httpClient<AdyenBusinessLine>({
      method: "GET",
      url: `/management/adyen/lem/business-lines/items/${id}`,
    });
  },

  createBusinessLine: async ({
    data,
  }: Omit<
    PostPayload<MutateAdyenBusinessLinePayload>,
    "organizationKeyName"
  >) => {
    return httpClient({
      method: "POST",
      url: `/management/adyen/lem/business-lines/items`,
      data: data,
    });
  },

  updateBusinessLine: async ({
    data: { id, ...rest },
  }: Omit<
    PutPayload<MutateAdyenBusinessLinePayload>,
    "organizationKeyName"
  >) => {
    return httpClient({
      method: "PUT",
      url: `/management/adyen/lem/business-lines/items/${id}`,
      data: rest,
    });
  },

  deleteBusinessLine: async ({
    data: { id },
  }: Omit<DeletePayload<{}>, "organizationKeyName">) => {
    return httpClient({
      method: "DELETE",
      url: `/management/adyen/lem/business-lines/items/${id}`,
    });
  },

  getBusinessLinesIndustryCodes: async () => {
    return httpClient<BusinessLineIndustryCode[]>({
      method: "GET",
      url: `/management/adyen/industry-codes/items`,
    });
  },

  getStores: async ({
    accountHolderId,
    organizationId,
  }: {
    accountHolderId?: string;
    organizationId?: string;
  }) => {
    const params = new URLSearchParams();
    if (accountHolderId) {
      params.append("accountHolderId", accountHolderId);
    }
    if (organizationId) {
      params.append("organizationId", organizationId);
    }
    return httpClient<AdyenStoreListItem[]>({
      method: "GET",
      url: `/management/adyen/management/stores/items?${params.toString()}`,
    });
  },

  getStore: async (id?: string) => {
    return httpClient<AdyenStore>({
      method: "GET",
      url: `/management/adyen/management/stores/items/${id}`,
    });
  },

  createStore: async ({
    data,
  }: Omit<PostPayload<MutateAdyenStorePayload>, "organizationKeyName">) => {
    return httpClient<{ id: string }>({
      method: "POST",
      url: `/management/adyen/management/stores/items`,
      data: data,
    });
  },

  updateStore: async ({
    data: { id, ...rest },
  }: Omit<PutPayload<MutateAdyenStorePayload>, "organizationKeyName">) => {
    return httpClient({
      method: "PUT",
      url: `/management/adyen/management/stores/items/${id}`,
      data: rest,
    });
  },

  pushAdyenStoreToAdyen: async ({
    data: { id },
  }: Omit<PostPayload<{ id: string }>, "organizationKeyName">) => {
    return httpClient({
      method: "POST",
      url: `/management/adyen/management/stores/items/${id}/push-to-adyen`,
    });
  },

  getBalanceAccounts: async (id?: string) => {
    return httpClient<AdyenBalanceAccountListItem[]>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}/balance-accounts/items`,
    });
  },

  getBalanceAccount: async (id?: string) => {
    return httpClient<AdyenBalanceAccount>({
      method: "GET",
      url: `/management/adyen/platform/balance-accounts/items/${id}`,
    });
  },

  createBalanceAccount: async ({
    data,
  }: Omit<
    PostPayload<MutateAdyenBalanceAccountPayload>,
    "organizationKeyName"
  >) => {
    return httpClient({
      method: "POST",
      url: `/management/adyen/platform/balance-accounts/items`,
      data: data,
    });
  },

  getSplitConfigurations: async () => {
    return httpClient<AdyenSplitConfigurationListItem[]>({
      method: "GET",
      url: `/management/adyen/management/split-configurations/items`,
    });
  },

  getSplitConfiguration: async (id?: string) => {
    return httpClient<AdyenSplitConfiguration>({
      method: "GET",
      url: `/management/adyen/management/split-configurations/items/${id}`,
    });
  },

  getPaymentInstruments: async (id?: string) => {
    return httpClient<AdyenAccountHolderPaymentInstrument[]>({
      method: "GET",
      url: `/management/adyen/platform/account-holders/items/${id}/transfer-instruments/items`,
    });
  },
};
